import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { PageHeader } from "../components/PageHeader";
import { RetryButton, StatePanel } from "../components/StatePanel";
import { StatusChip } from "../components/StatusChip";
import { api, formatDate, mutationKey } from "../lib/api";

interface Policy {
  readonly id: string;
  readonly policy_type: string;
  readonly version: number;
  readonly state: string;
  readonly effective_at: string;
  readonly published_at: string | null;
  readonly config_json: string;
}

const policyTemplates = {
  allowance: {
    min_slots: 1,
    max_slots: 12,
    plants_per_slot: 20,
    reporting_window_days: 365,
  },
  points: {
    rules: [
      {
        code: "verified_original",
        amount: 50,
        enabled: true,
        cooldown_days: 0,
      },
      {
        code: "verified_follow_up",
        amount: 15,
        enabled: true,
        cooldown_days: 30,
      },
    ],
  },
  badges: {
    badges: [
      {
        code: "seedling_guardian",
        name: "Seedling Guardian",
        threshold_points: 100,
      },
    ],
  },
  leaderboard: {
    primary: "verified_points",
    tie_breakers: [
      "verified_follow_ups",
      "verified_plant_quantity",
      "score_reached_at",
      "entity_id",
    ],
    public_individual_opt_in: true,
  },
  geofence: {
    default_tolerance_m: 25,
    maximum_accuracy_m: 100,
    outside_requires_human_review: true,
  },
  measurement: {
    metrics: {
      average_height_cm: {
        minimum: 0,
        maximum: 5000,
        required: true,
        unit: "cm",
      },
      average_dbh_cm: {
        minimum: 0,
        maximum: 1000,
        required: false,
        unit: "cm",
      },
    },
  },
  minors: {
    guardian_consent_required: true,
    school_managed_accounts: true,
    public_name_required: true,
    exact_location_private: true,
    public_media_visible: false,
  },
  public_identity: {
    default_to_public_display_name: true,
    contact_details_public: false,
    minor_organization_details_public: false,
  },
  retention: {
    rejected_media_days: 30,
    generated_export_days: 1,
    audit_history: "append_only",
    allowance_history: "append_only",
    point_history: "append_only",
  },
  features: {
    physical_rewards: false,
    qr_tracking: false,
    community_feed: true,
  },
} as const;

type PolicyType = keyof typeof policyTemplates;

function templateJson(type: PolicyType): string {
  return JSON.stringify(policyTemplates[type], null, 2);
}

export function PoliciesPage() {
  const [creating, setCreating] = useState(false);
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["policies"],
    queryFn: () =>
      api<{ readonly items: readonly Policy[] }>("/admin/policies"),
  });
  return (
    <>
      <PageHeader
        eyebrow="Administration"
        title="Program policies"
        description="Draft, validate, compare, and publish effective-dated calculation and safeguarding rules."
        actions={
          <button
            className="button primary"
            type="button"
            onClick={() => setCreating((value) => !value)}
          >
            {creating ? "Close draft form" : "New policy draft"}
          </button>
        }
      />
      {creating ? (
        <PolicyDraftForm
          onCreated={async () => {
            setCreating(false);
            await queryClient.invalidateQueries({ queryKey: ["policies"] });
          }}
        />
      ) : null}
      {query.isLoading ? (
        <StatePanel
          kind="loading"
          title="Loading policy register"
          detail="Reading draft and published versions."
        />
      ) : query.isError || query.data === undefined ? (
        <StatePanel
          kind="error"
          title="Policies are unavailable"
          detail="No draft or published rule was changed."
          action={<RetryButton onClick={() => void query.refetch()} />}
        />
      ) : query.data.items.length === 0 ? (
        <StatePanel
          kind="empty"
          title="No policy versions"
          detail="Create draft allowance, point, badge, leaderboard, geofence, and measurement rules before UAT."
        />
      ) : (
        <div className="record-list">
          {query.data.items.map((policy) => {
            const previousPublished = query.data.items.find(
              (candidate) =>
                candidate.id !== policy.id &&
                candidate.policy_type === policy.policy_type &&
                candidate.state === "published" &&
                new Date(candidate.effective_at).getTime() <= Date.now(),
            );
            return (
              <PolicyRecord
                key={policy.id}
                policy={policy}
                previousPublished={previousPublished}
              />
            );
          })}
        </div>
      )}
    </>
  );
}

function PolicyRecord({
  policy,
  previousPublished,
}: {
  readonly policy: Policy;
  readonly previousPublished: Policy | undefined;
}) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");
  const publish = useMutation({
    mutationFn: (publicationReason: string) =>
      api(`/admin/policies/${policy.id}/publish`, {
        method: "POST",
        idempotencyKey: mutationKey("policy-publish"),
        body: JSON.stringify({
          expected_state: "draft",
          reason: publicationReason,
        }),
      }),
    onSuccess: async () => {
      setConfirming(false);
      setReason("");
      await queryClient.invalidateQueries({ queryKey: ["policies"] });
    },
  });
  return (
    <article className="record-row">
      <div>
        <div className="record-title">
          <h2>{policy.policy_type.replaceAll("_", " ")}</h2>
          <StatusChip state={policy.state} />
        </div>
        <p>Version {policy.version}</p>
        <small>Effective {formatDate(policy.effective_at)}</small>
        <details>
          <summary>Validated configuration</summary>
          <pre>{JSON.stringify(JSON.parse(policy.config_json), null, 2)}</pre>
        </details>
      </div>
      <div>
        <span className="quiet">
          {policy.published_at === null
            ? "Not affecting calculations"
            : `Published ${formatDate(policy.published_at)}`}
        </span>
        {policy.state === "draft" ? (
          <button
            className="button secondary"
            type="button"
            disabled={publish.isPending}
            onClick={() => setConfirming(true)}
          >
            Review and publish
          </button>
        ) : null}
        {publish.isError && !confirming ? (
          <small className="form-error" role="alert">
            {publish.error.message}
          </small>
        ) : null}
      </div>
      {confirming ? (
        <div className="dialog-layer" role="presentation">
          <section
            className="dialog policy-publish-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`publish-policy-${policy.id}`}
          >
            <div className="dialog-title">
              <div>
                <p className="eyebrow">Effective policy change</p>
                <h2 id={`publish-policy-${policy.id}`}>
                  Publish {policy.policy_type.replaceAll("_", " ")} version{" "}
                  {policy.version}
                </h2>
              </div>
            </div>
            <p>
              This version becomes eligible at its effective time. Earlier
              published versions remain immutable, and the server selects the
              latest version already in effect without rewriting ledger history.
            </p>
            <div className="policy-diff" aria-label="Policy configuration diff">
              <section>
                <h3>Currently published</h3>
                {previousPublished === undefined ? (
                  <p className="quiet">No published version exists.</p>
                ) : (
                  <pre>
                    {JSON.stringify(
                      JSON.parse(previousPublished.config_json),
                      null,
                      2,
                    )}
                  </pre>
                )}
              </section>
              <section>
                <h3>Draft version {policy.version}</h3>
                <pre>
                  {JSON.stringify(JSON.parse(policy.config_json), null, 2)}
                </pre>
              </section>
            </div>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                publish.mutate(reason.trim());
              }}
            >
              <label>
                Publication reason
                <textarea
                  required
                  minLength={3}
                  maxLength={500}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              </label>
              {publish.isError ? (
                <p className="form-error" role="alert">
                  {publish.error.message}
                </p>
              ) : null}
              <div className="dialog-actions">
                <button
                  className="button secondary"
                  type="button"
                  disabled={publish.isPending}
                  onClick={() => setConfirming(false)}
                >
                  Cancel
                </button>
                <button
                  className="button primary"
                  type="submit"
                  disabled={publish.isPending || reason.trim().length < 3}
                >
                  {publish.isPending ? "Publishing…" : "Confirm publication"}
                </button>
              </div>
            </form>
          </section>
        </div>
      ) : null}
    </article>
  );
}

function PolicyDraftForm({
  onCreated,
}: {
  readonly onCreated: () => Promise<void>;
}) {
  const [validationError, setValidationError] = useState<string | null>(null);
  const [policyType, setPolicyType] = useState<PolicyType>("allowance");
  const [configuration, setConfiguration] = useState(() =>
    templateJson("allowance"),
  );
  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api("/admin/policies", {
        method: "POST",
        idempotencyKey: mutationKey("policy-draft"),
        body: JSON.stringify(body),
      }),
    onSuccess: onCreated,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setValidationError(null);
    const data = new FormData(event.currentTarget);
    const effectiveAt = data.get("effective_at");
    const config = data.get("config");
    try {
      if (typeof effectiveAt !== "string" || typeof config !== "string") {
        throw new Error("Policy form is incomplete.");
      }
      create.mutate({
        policy_type: data.get("policy_type"),
        version: Number(data.get("version")),
        effective_at: new Date(effectiveAt).toISOString(),
        config: JSON.parse(config) as unknown,
      });
    } catch {
      create.reset();
      setValidationError(
        "Configuration must be valid JSON before the draft can be saved.",
      );
    }
  };
  return (
    <section className="panel">
      <h2>Create validated draft</h2>
      <form className="report-form" onSubmit={submit}>
        <label>
          Policy type
          <select
            name="policy_type"
            value={policyType}
            onChange={(event) => {
              const next = event.target.value as PolicyType;
              setPolicyType(next);
              setConfiguration(templateJson(next));
              setValidationError(null);
              create.reset();
            }}
          >
            {(Object.keys(policyTemplates) as PolicyType[]).map((type) => (
              <option key={type} value={type}>
                {type.replaceAll("_", " ")}
              </option>
            ))}
          </select>
        </label>
        <label>
          Version
          <input name="version" type="number" min="1" required />
        </label>
        <label>
          Effective at
          <input name="effective_at" type="datetime-local" required />
        </label>
        <label className="wide-field">
          Configuration JSON
          <textarea
            name="config"
            rows={10}
            value={configuration}
            onChange={(event) => setConfiguration(event.target.value)}
            aria-invalid={validationError !== null}
            aria-describedby={`policy-config-help${
              validationError === null ? "" : " policy-config-error"
            }`}
            required
          />
        </label>
        <span className="quiet wide-field" id="policy-config-help">
          The server rejects unknown fields, unsafe feature flags, duplicate
          rules, and policy values that do not match the documented formula.
        </span>
        {validationError === null ? null : (
          <p
            className="form-error wide-field"
            id="policy-config-error"
            role="alert"
          >
            {validationError}
          </p>
        )}
        <button
          className="button primary"
          type="submit"
          disabled={create.isPending}
        >
          Save draft
        </button>
      </form>
      {create.isError ? (
        <p className="form-error" role="alert">
          {create.error.message}
        </p>
      ) : null}
    </section>
  );
}
